import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '../../services/translate.service';
import { BorderIconButtonComponent } from '../buttons/border-icon-button.component';
import { CircleButtonComponent } from '../buttons/circle-button.component';
import { BottomSheetService } from '../../services/bottom-sheet.service';
import { LoadingSpinnerComponent } from './loading-spinner-component';
import { LangConfirmService } from '../../services/lang-confirm.service';
import {ConfirmLangDialogComponent} from './confirm-lang-dialog.component';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {LinkButtonComponent} from '../buttons/link-button.component';

@Component({
  standalone: true,
  imports: [CommonModule, BorderIconButtonComponent, CircleButtonComponent, LoadingSpinnerComponent, ConfirmLangDialogComponent, ActionButtonComponent, LinkButtonComponent],
  selector: 'app-lang-push',
  template: `
    <div *ngIf="!isLoading && !showConfirmDialog" class="w-full">
      <div class="flex flex-col gap-4 pb-8 pt-4">
        <div>{{translationsMap['original']}}</div>
        <div class="mb-4">{{translationsMap['useTranslation']}}</div>
        <app-action-button text="{{languagesMap.get(destLang)?.flag}}&nbsp;&nbsp;{{translationsMap['yes']}}" (onClick)="selectLang()" [chevron]="false"></app-action-button>
        <app-border-icon-button text="{{languagesMap.get(translate.venueLang)?.flag}}&nbsp;&nbsp;{{translationsMap['keep']}}" (onClick)="useSwedish()"></app-border-icon-button>
        <div class="flex justify-center items-center mt-4">
          <app-link-button text="{{translationsMap['selectOther']}}" [font]="'font-bold'" (onClick)="openLangSelectSheet()" leftIcon="fa-language"></app-link-button>
        </div>
      </div>
    </div>

    <div *ngIf="isLoading" class="flex justify-center items-center py-16">
      <app-loading-spinner></app-loading-spinner>
    </div>

    <!-- Language confirmation dialog -->
    <div *ngIf="showConfirmDialog" class="">
      <app-confirm-lang-dialog
        [lang]="languagesMap.get(destLang)"
        (onClose)="closeConfirmDialog()"
        (onConfirm)="confirmLangChange($event)"
        [buttonText]="translationsMap['yes']"
        >
      </app-confirm-lang-dialog>
    </div>
  `
})
export class LangPushComponent implements OnInit {
  public isLoading: boolean = false;
  public showConfirmDialog: boolean = false;
  public selectedLang: any = null;
  destLang: string = "sv"; // Default to Swedish
  strKeepSwedish: string = "";
  translationsMap: { [key: string]: string } = {};

  languagesMap = new Map([
    ["sv", {code: "sv", name: "Svenska", flag: "🇸🇪"}],
    ["en", {code: "en", name: "English", flag: "🇬🇧"}],
    ["zh", {code: "zh", name: "汉语", flag: "🇨🇳"}],
    ["es", {code: "es", name: "Español", flag: "🇪🇸"}],
    ["fr", {code: "fr", name: "Français", flag: "🇫🇷"}],
    ["de", {code: "de", name: "Deutsch", flag: "🇩🇪"}],
    ["pt", {code: "pt", name: "Português", flag: "🇵🇹"}],
    ["it", {code: "it", name: "Italiano", flag: "🇮🇹"}],
    ["ja", {code: "ja", name: "日本語", flag: "🇯🇵"}],
    ["ko", {code: "ko", name: "한국어", flag: "🇰🇷"}],
    ["ar", {code: "ar", name: "العربية", flag: "🇸🇦"}],
    ["tr", {code: "tr", name: "Türkçe", flag: "🇹🇷"}],
    ["vi", {code: "vi", name: "Tiếng Việt", flag: "🇻🇳"}],
    ["nl", {code: "nl", name: "Dutch", flag: "🇳🇱"}],
    ["da", {code: "da", name: "Dansk", flag: "🇩🇰"}],
    ["fi", {code: "fi", name: "Suomi", flag: "🇫🇮"}],
    ["no", {code: "no", name: "Norsk", flag: "🇳🇴"}],
    ["is", {code: "is", name: "Íslenska", flag: "🇮🇸"}],
    ["he", {code: "he", name: "עברית", flag: "🇮🇱"}],
    ["hi", {code: "hi", name: "हिन्दी", flag: "🇮🇳"}],
    ["id", {code: "id", name: "Bahasa Indonesia", flag: "🇮🇩"}],
    ["mr", {code: "mr", name: "मराठी", flag: "🇮🇳"}],
    ["tl", {code: "tl", name: "Tagalog", flag: "🇵🇭"}],
    ["pl", {code: "pl", name: "Polski", flag: "🇵🇱"}],
    ["sk", {code: "sk", name: "Slovenský", flag: "🇸🇰"}],
    ["th", {code: "th", name: "ภาษาไทย", flag: "🇹🇭"}],
    ["uk", {code: "uk", name: "Українська", flag: "🇺🇦"}],
  ]);

  constructor(
    protected translate: TranslateService,
    private langConfirmService: LangConfirmService,
    private sheetService: BottomSheetService
  ) { }

  ngOnInit(): void {
    this.destLang = this.translate.getBrowserLang();
    this.translate.observeLangPushScreen(this.destLang).subscribe((translations) => {
      this.translationsMap = this.createTranslationsMap(translations);

      //Update yes string, replace %s with the actual language name
      this.translationsMap['yes'] = this.translationsMap['yes'].replace('%s', this.languagesMap.get(this.destLang)?.name || 'language');
      // "keep" and "original" name the venue's language, not always Swedish
      const venueLangName = this.languagesMap.get(this.translate.venueLang)?.name || this.translate.venueLang;
      this.translationsMap['keep'] = this.translationsMap['keep']?.replace('%s', venueLangName);
      this.translationsMap['original'] = this.translationsMap['original']?.replace('%s', venueLangName);
    });
  }

  private createTranslationsMap(translations: any[]): { [key: string]: string } {
    const map: { [key: string]: string } = {};

    // Map each translation to a descriptive key
    translations.forEach(translation => {
      switch(translation.phrase) {
        case this.translate.LANG_PUSH_SELECT_LANGUAGE:
          map['select'] = translation.translation || translation.phrase;
          break;
        case this.translate.LANG_PUSH_YES_SWITCH:
          map['yes'] = translation.translation || translation.phrase;
          break;
        case this.translate.LANG_PUSH_NO_KEEP_SWEDISH:
          map['keep'] = translation.translation || translation.phrase;
          break;
        case this.translate.LANG_PUSH_ORIGINAL_LANGUAGE:
          map['original'] = translation.translation || translation.phrase;
          break;
        case this.translate.LANG_PUSH_USE_TRANSLATION:
          map['useTranslation'] = translation.translation || translation.phrase;
          break;
        case this.translate.LANG_PUSH_SELECT_OTHER:
          map['selectOther'] = translation.translation || translation.phrase;
          break;
      }
    });

    return map;
  }

  selectLang() {
    this.showConfirmDialog = true;
  }

  closeConfirmDialog() {
    this.showConfirmDialog = false;
    this.selectedLang = null;
  }

  confirmLangChange(_lang: any) {
    // Mark that we've shown the language push
    localStorage.setItem("collinsLang", this.destLang);
    window.location.reload();
  }

  openLangSelectSheet() {
    this.sheetService.replaceSheet({sheetClass: "select-lang", title: this.translate.getString("Språk")});
  }

  useSwedish() {
    // Mark that we've shown the language push
    this.sheetService.popSheet();
  }
}
